<script setup>
import { computed, nextTick, ref, watch } from "vue";
import { Link, useForm, usePage } from "@inertiajs/vue3";
import axios from "axios";
import { CheckIcon } from "@heroicons/vue/24/outline";
import Modal from "@/Components/Modal.vue";
import Button from "@/Components/Button.vue";
import InputError from "@/Components/InputError.vue";
import CountryPicker from "@/Components/CountryPicker.vue";
import { countrySuggestions, fetchEventTrackAlertCountries } from "@/Helpers/helperFunctions.js";

/**
 * "Get alerts for new events & tracks". One instance lives in AppLayout and is opened with
 * eventBus.$emit("showEventTrackAlertModal", { initialCountries, initialTypes }). The email manage page
 * also passes its `email` and `token`: the token proves that inbox, so it subscribes at once.
 */
const props = defineProps({
    show: { type: Boolean, default: false },
    initialCountries: { type: Array, default: () => [] }, // ISO2, e.g. ["LV"]
    initialTypes: { type: Array, default: () => ["events", "tracks"] },
    email: { type: String, default: null },
    token: { type: String, default: null },
});
const emit = defineEmits(["close"]);
const page = usePage();

const TYPES = [
    { id: "events", title: "New events", subtitle: "When an event is published", noun: "event" },
    { id: "tracks", title: "New tracks", subtitle: "When a track is published", noun: "track" },
];

const data = ref({ countries: [], near: [], current: null });
const names = computed(() => Object.fromEntries(data.value.countries.map((c) => [c.id, c.name])));

const form = useForm({
    email: "",
    countries: [],
    notify_events: true,
    notify_tracks: true,
    token: null,
});
const editingEmail = ref(false);
const status = ref(null); // null = form, "subscribed" | "confirm" = success screen
const undoFailed = ref(false);
const loaded = ref(false); // `data.current` (for "Undo") is known only after load(), so submit waits for it
const loadFailed = ref(false);

const panel = ref(null);
const picker = ref(null);
const emailInput = ref(null);

const selectedTypes = computed(() => TYPES.filter((t) => form[`notify_${t.id}`]));
const suggestions = computed(() => countrySuggestions(data.value.countries, form.countries, data.value.near));
// The `countries` field itself or any `countries.N` entry.
const countriesError = computed(
    () => form.errors.countries ?? Object.entries(form.errors).find(([key]) => key.startsWith("countries."))?.[1],
);

const problem = computed(() => {
    if (!form.countries.length) {
        return "Add at least one country.";
    }
    if (!selectedTypes.value.length) {
        return "Choose events, tracks, or both.";
    }
    return null;
});

const joinOr = (items) => new Intl.ListFormat("en", { type: "disjunction" }).format(items);

const summary = computed(() => {
    const nouns = selectedTypes.value.map((t) => t.noun);
    return (
        `You'll get an email when a new ${joinOr(nouns)} is published in ` +
        `${joinOr(form.countries.map((id) => names.value[id] ?? id))}.`
    );
});

async function open() {
    loaded.value = false;
    status.value = null;
    undoFailed.value = false;
    picker.value?.clear();
    form.clearErrors();
    form.email = props.email ?? page.props.user?.email ?? "";
    form.token = props.token;
    editingEmail.value = !form.email;
    // Page country (track/event) first, else the profile country. The browser time zone comes after load().
    form.countries = supported(props.initialCountries);
    if (!form.countries.length) {
        form.countries = supported([page.props.user?.country]);
    }
    form.notify_events = props.initialTypes.includes("events");
    form.notify_tracks = props.initialTypes.includes("tracks");

    await nextTick();
    picker.value?.focus();
    await load();
}

function supported(codes) {
    const unsupported = page.props.unsupportedCountries ?? [];
    return codes
        .filter(Boolean)
        .map((c) => c.toUpperCase())
        .filter((c) => !unsupported.includes(c));
}

// Load on every open: the request also starts the server-side fill timer in a fresh session.
async function load() {
    loadFailed.value = false;
    try {
        data.value = await fetchEventTrackAlertCountries();
    } catch {
        loadFailed.value = true;
        return;
    }
    loaded.value = true;
    // Start with the geo-IP / browser time zone country (nearCountries() on the server).
    if (!form.countries.length && data.value.near.length) {
        form.countries = [data.value.near[0]];
    }
}

watch(
    () => props.show,
    (show) => show && open(),
);

function addCountry(id) {
    if (!form.countries.includes(id)) {
        form.countries = [...form.countries, id];
    }
}

function toggleType(id) {
    form[`notify_${id}`] = !form[`notify_${id}`];
}

async function changeEmail() {
    editingEmail.value = true;
    await nextTick();
    emailInput.value?.focus();
}

// The base Modal closes on Esc but does not trap focus, so keep Tab inside the panel.
function trapFocus(event) {
    if (event.key !== "Tab" || !panel.value) {
        return;
    }
    const focusable = [
        ...panel.value.querySelectorAll('a[href], button:not([disabled]), input:not([tabindex="-1"]), [tabindex="0"]'),
    ].filter((el) => el.offsetParent !== null);
    const first = focusable[0];
    const last = focusable.at(-1);
    if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
    }
}

function submit() {
    if (problem.value || !loaded.value) {
        return;
    }
    form.post(route("event-track-alerts.store"), {
        preserveScroll: true,
        preserveState: true,
        onSuccess: (page) => {
            status.value = page.props.jetstream?.flash?.eventTrackAlertStatus ?? "confirm";
        },
        onError: (errors) => errors.session && load(),
    });
}

// Only after "subscribed" (the signed-in user's own email): put back the exact rows from before.
async function undo() {
    undoFailed.value = false;
    try {
        await axios.put(route("api.event-track-alerts.replace"), { rows: data.value.current ?? [] });
    } catch {
        undoFailed.value = true;
        return;
    }
    status.value = null;
}
</script>

<template>
    <Modal
        :show="show"
        max-width="lg"
        @close="emit('close')"
    >
        <div
            ref="panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="notify-me-title"
            @keydown="trapFocus"
        >
            <!-- Success -->
            <div
                v-if="status"
                class="flex flex-col items-center gap-3 px-6 py-8 text-center"
            >
                <div class="flex size-12 items-center justify-center rounded-full bg-green-100 text-green-600">
                    <CheckIcon class="size-7" />
                </div>
                <h2
                    id="notify-me-title"
                    class="text-lg font-semibold text-gray-900"
                >
                    {{ status === "subscribed" ? "You're subscribed" : "Check your email to confirm" }}
                </h2>
                <InputError
                    v-if="undoFailed"
                    message="Could not undo. Try again."
                />
                <p
                    v-if="status === 'subscribed' && token"
                    class="text-sm text-gray-600"
                >
                    {{ summary }} You can change it on this page.
                </p>
                <p
                    v-else-if="status === 'subscribed'"
                    class="text-sm text-gray-600"
                >
                    {{ summary }} Manage anytime in
                    <Link
                        :href="route('profile.show')"
                        class="text-blue-700 hover:underline"
                        @click="emit('close')"
                        >Profile → Notification preferences</Link
                    >
                </p>
                <p
                    v-else
                    class="text-sm text-gray-600"
                >
                    We sent a confirmation link to <strong>{{ form.email }}</strong>. You will not get any emails until
                    you click it.
                </p>
            </div>

            <!-- Form -->
            <form
                v-else
                id="notify-me-form"
                class="flex flex-col gap-5 px-6 py-5"
                @submit.prevent="submit"
            >
                <div>
                    <h2
                        id="notify-me-title"
                        class="text-lg font-semibold text-gray-900"
                    >
                        Get alerts for new events &amp; tracks
                    </h2>
                    <p class="mt-1 text-sm text-gray-500">Pick countries and what to hear about. Unsubscribe anytime.</p>
                </div>

                <!-- Email -->
                <div>
                    <label
                        for="notify-me-email"
                        class="mb-1 block text-sm font-medium text-gray-700"
                        >Email</label
                    >
                    <div
                        v-if="!editingEmail"
                        class="flex items-center justify-between rounded-md bg-gray-100 px-3 py-2 text-sm"
                    >
                        <span class="truncate text-gray-800">{{ form.email }}</span>
                        <button
                            type="button"
                            class="ml-3 shrink-0 text-blue-700 hover:underline"
                            @click="changeEmail"
                        >
                            Change
                        </button>
                    </div>
                    <input
                        v-else
                        id="notify-me-email"
                        ref="emailInput"
                        v-model="form.email"
                        type="email"
                        required
                        autocomplete="email"
                        placeholder="you@example.com"
                        class="w-full rounded-md border-gray-300 text-sm shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        :class="{ 'border-red-600': form.errors.email }"
                    />
                    <InputError
                        :message="form.errors.email"
                        class="mt-1"
                    />
                </div>

                <!-- Countries -->
                <div>
                    <CountryPicker
                        ref="picker"
                        v-model="form.countries"
                        :countries="data.countries"
                        :error="countriesError"
                    />
                    <div
                        v-if="suggestions.ids.length"
                        class="mt-2 flex flex-wrap items-center gap-2 text-sm"
                    >
                        <span class="text-gray-500">{{ suggestions.label }}:</span>
                        <button
                            v-for="id in suggestions.ids"
                            :key="id"
                            type="button"
                            class="rounded-full border border-dashed border-gray-400 px-2.5 py-0.5 text-gray-700 hover:border-blue-500 hover:text-blue-700"
                            @click="addCountry(id)"
                        >
                            + {{ names[id] ?? id }}
                        </button>
                    </div>
                </div>

                <!-- Types -->
                <div>
                    <div class="mb-1 text-sm font-medium text-gray-700">Notify me about</div>
                    <div class="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div
                            v-for="type in TYPES"
                            :key="type.id"
                            role="checkbox"
                            tabindex="0"
                            :aria-checked="form[`notify_${type.id}`]"
                            class="flex cursor-pointer items-start gap-3 rounded-lg border-2 p-3 focus:outline-none focus-visible:ring focus-visible:ring-blue-200"
                            :class="form[`notify_${type.id}`] ? 'border-blue-600 bg-blue-50' : 'border-gray-200 hover:border-gray-300'"
                            @click="toggleType(type.id)"
                            @keydown.space.prevent="toggleType(type.id)"
                        >
                            <span
                                class="mt-0.5 flex size-4 shrink-0 items-center justify-center rounded border"
                                :class="form[`notify_${type.id}`] ? 'border-blue-600 bg-blue-600 text-white' : 'border-gray-400 bg-white'"
                            >
                                <CheckIcon
                                    v-if="form[`notify_${type.id}`]"
                                    class="size-3 stroke-[3]"
                                />
                            </span>
                            <span>
                                <span class="block text-sm font-medium text-gray-900">{{ type.title }}</span>
                                <span class="block text-xs text-gray-500">{{ type.subtitle }}</span>
                            </span>
                        </div>
                    </div>
                    <InputError
                        :message="form.errors.notify_events"
                        class="mt-1"
                    />
                </div>

                <!-- Live summary -->
                <div
                    aria-live="polite"
                    class="rounded-lg px-3 py-2 text-[13px]"
                    :class="problem ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-700'"
                >
                    {{ problem ?? summary }}
                </div>
                <InputError :message="form.errors.session" />
                <p
                    v-if="loadFailed"
                    class="text-sm text-red-600"
                >
                    Could not load the country list.
                    <button
                        type="button"
                        class="font-medium underline"
                        @click="load"
                    >
                        Try again
                    </button>
                </p>
            </form>

            <div class="flex flex-row justify-end gap-3 bg-gray-100 px-6 py-4">
                <template v-if="status">
                    <Button
                        v-if="status === 'subscribed' && !token"
                        color="white"
                        class-name="px-5 py-2"
                        @click="undo"
                    >
                        Undo
                    </Button>
                    <Button
                        color="primary"
                        class-name="px-5 py-2"
                        @click="emit('close')"
                    >
                        Done
                    </Button>
                </template>
                <template v-else>
                    <Button
                        color="white"
                        class-name="px-5 py-2"
                        @click="emit('close')"
                    >
                        Cancel
                    </Button>
                    <Button
                        type="submit"
                        form="notify-me-form"
                        color="primary"
                        class-name="px-5 py-2"
                        :disabled="!!problem || !loaded || form.processing"
                        :class="{ 'pointer-events-none opacity-[.45]': problem || !loaded || form.processing }"
                    >
                        Notify me
                    </Button>
                </template>
            </div>
        </div>
    </Modal>
</template>
